import { Dialog, Heading } from '@madpaws/design-system';
import { useGoogleOneTapLogin } from '@react-oauth/google';
import { useRouter } from 'next/router';
import React from 'react';

import { FORM_IDS } from '~/common/constants/app';
import { DOM_CONTAINER_ID } from '~/components/constants';
import { AuthApi } from '~/services/auth/authApi';
import { sentryException } from '~/utils/sentryHelpers';

import styles from './AuthDialog.module.css';
import { EmailVerificationForm } from './EmailVerificationForm';
import { FacebookLoginButton } from './FacebookLoginButton';
import { GoogleLoginButton } from './GoogleLoginButton';
import { AuthMethods, WHITE_LIST_OF_ROUTES } from '../constants';
import { getSocialLoginArgs } from '../helpers/getSocialLoginArgs';
import { useAuthenticationContext } from '../useAuthenticationContext';

import type { OnLoginSuccessHandler, OnSocialSignUpHandler } from '../types';
import type { ReactElement } from 'react';
import type {
  LoginSocialExistingUserRestResponse,
  LoginSocialNewUserRestResponse,
  SocialLoginArgs,
} from '~/services/auth/types';

export type Props = {
  onLoginSuccess: OnLoginSuccessHandler;
  onSocialSignUp: OnSocialSignUpHandler;
  title: string;
};

export type SocialSignUpProps = {
  firstName?: string;
  lastName?: string;
  newUserAcceptedTerms?: boolean;
  receiveSpecialOffers?: boolean;
  socialToken: string;
};

export const AuthDialog = ({ title, onLoginSuccess, onSocialSignUp }: Props): ReactElement => {
  const { setIsAuthDialogOpen, isAuthDialogOpen, isUserLoggedIn } = useAuthenticationContext();

  const router = useRouter();

  const sendSentryException = (error: unknown, socialLoginArgs: SocialLoginArgs): void => {
    sentryException(
      error,
      `signInWithSocial API failed, socialLoginArgs: ${JSON.stringify(socialLoginArgs, null, 2)}`
    );
  };

  const {
    route: routerRoute,
    query: { refSource, referralCode },
  } = router;

  const submitSocialSignIn = async (
    socialToken: string,
    authMethod: AuthMethods,
    signUpProps?: SocialSignUpProps
  ): Promise<void> => {
    const extraArgs = {
      one_tap: routerRoute,
      refSource: (refSource as string) ?? null,
      referralCode: (referralCode as string) ?? null,
    };
    const socialLoginArgs: SocialLoginArgs = (await getSocialLoginArgs(
      socialToken,
      authMethod,
      signUpProps,
      extraArgs
    )) as SocialLoginArgs;

    try {
      const response = await AuthApi.loginWithSocials(socialLoginArgs, authMethod as AuthMethods);

      if (!response) {
        throw new Error(`Could not log in with ${authMethod}. Response is empty.`);
      }

      const {
        userNeedsToAcceptTerms,
        // isUserPhoneNumberVerified
      } = response;

      if (userNeedsToAcceptTerms) {
        onSocialSignUp(response as LoginSocialNewUserRestResponse, authMethod);
        setIsAuthDialogOpen(false);

        return;
      }

      const existingUserResponse = response as LoginSocialExistingUserRestResponse;

      // To reenalbe one 2FA is back
      // Cookies.set(
      // ACCESS_TOKEN_COOKIE_NAME, JSON.stringify({ tokens: response }) ?? ''
      // );
      // setLocalStorage(MD_AUTH_LOCAL_STORAGE_KEY, { ...response });
      // RestApi.setAuthorizationHeader({
      //   authorization: `Bearer ${existingUserResponse.access_token}`,
      // });

      // Show the social sign up dialog if user has unverified phone number
      // if (isUserPhoneNumberVerified === false) {
      //   onSocialSignUpRequired(response, authMethod);

      //   return;
      // }

      onLoginSuccess(
        {
          access_token: existingUserResponse.access_token as string,
          expires_in: existingUserResponse.expires_in as number,
          refresh_token: existingUserResponse.refresh_token as string,
          token_type: existingUserResponse.token_type as string,
        },
        { email: existingUserResponse.email as string, userId: existingUserResponse.userId },
        authMethod
      );

      setIsAuthDialogOpen(false);
    } catch (error) {
      sendSentryException(error, socialLoginArgs);
    }
  };

  const onDialogClose = (): void => {
    setIsAuthDialogOpen(false);
  };

  const isEnabled = WHITE_LIST_OF_ROUTES.includes(router.pathname) && !isUserLoggedIn;

  useGoogleOneTapLogin({
    onSuccess: (credentialResponse) => {
      submitSocialSignIn(credentialResponse.credential as string, AuthMethods.GOOGLE_ONE_TAP);
    },
    onError: () => {
      console.info('[AuthDialog] Google One Tap prompt failed');
    },
    disabled: !isEnabled,
    cancel_on_tap_outside: false,
    use_fedcm_for_prompt: true,
  });

  return (
    <>
      <Dialog
        domContainerId={DOM_CONTAINER_ID}
        formId={FORM_IDS.SIGN_UP_FORM}
        heading="Welcome to Mad Paws"
        isOpen={isAuthDialogOpen}
        onRequestClose={onDialogClose}
      >
        <div className={styles.dialogContainer}>
          <Heading alignment="centerAlign" level={4} size="small2x">
            {title || `Create a free account or log in`}
          </Heading>
          <GoogleLoginButton onLoginSuccess={submitSocialSignIn} />
          <FacebookLoginButton onLoginSuccess={submitSocialSignIn} />
          <div className={styles.divider}>
            <span className={styles.hr} />
            <p>or continue with email</p>
            <span className={styles.hr} />
          </div>
          <EmailVerificationForm />
          <div className={styles.recaptchaDisclaimer}>
            This site is protected by reCAPTCHA and the Google
            <a href="https://policies.google.com/privacy" rel="noreferrer" target="_blank">
              {' '}
              Privacy Policy{' '}
            </a>{' '}
            and
            <a href="https://policies.google.com/terms" rel="noreferrer" target="_blank">
              {' '}
              Terms of Service{' '}
            </a>{' '}
            apply.
          </div>
        </div>
      </Dialog>
    </>
  );
};
